import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import store from '../shared/redux/store';
import authSlice, { selectIdToken, selectRefreshToken } from '../auth/redux/slice';

type RetriableRequestConfig = InternalAxiosRequestConfig & { _retriedAfterRefresh?: boolean };

// Sep 2026 - "session gets timed out so frequently... lets research it completely" - the JWT
// access token (server-side TokenService.ExpirationMinutes = 60) previously had no renewal path
// at all: the moment it expired, the only way back in was a full username/password re-login. This
// module-level mutex ensures a burst of concurrent 401s (every in-flight authenticated request
// failing around the same moment, once the access token dies) triggers exactly ONE network call to
// POST /api/auth/refresh, not one per failed request - every concurrent 401 awaits the SAME
// in-flight promise instead of racing to refresh independently (which would also race each other's
// single-use token rotation server-side, per RefreshToken.cs's own comment - the second one to land
// would look like a replayed/reused token and trigger theft-containment for no real reason).
//
// Sep 2026 - "we have 2 records for one user... lets fix this" - the in-flight-promise mutex above
// only de-dupes requests that check WHILE a refresh is still pending. A burst of several 401s (e.g.
// 5 GETs firing on page load right after the access token died) can still produce MORE than one
// real refresh call if the first one finishes fast enough: a later 401 in the same burst might not
// get its turn to check `refreshPromise` until AFTER it's already been cleared by the first
// refresh's `.finally()` - at that point it sees nothing in flight and kicks off its own, fully
// valid but redundant, second refresh (each one single-use-rotating the token again - not a
// security bug, just wasteful DB churn and confusing to look at). Fixed below by checking whether
// THIS failed request's own token is already stale relative to the store's CURRENT token before
// ever considering a new refresh - if some other request already refreshed in the meantime, this
// one just retries with the current token directly, no second network call at all.
let refreshPromise: Promise<string | null> | null = null;

function getBearerToken(headers: any): string | null {
    const raw = typeof headers?.get === 'function' ? headers.get('Authorization') : headers?.['Authorization'];
    if (typeof raw !== 'string')
        return null;
    const match = raw.match(/^Bearer\s+(.+)$/i);
    return match ? match[1] : null;
}

// See the header-mutation comment on the interceptor below for why `.set()` vs bracket assignment.
function setBearerToken(config: RetriableRequestConfig, token: string) {
    if (config.headers && typeof (config.headers as any).set === 'function') {
        (config.headers as any).set('Authorization', `Bearer ${token}`);
    } else {
        config.headers = { ...config.headers, Authorization: `Bearer ${token}` } as any;
    }
}

async function performRefresh(baseURL?: string): Promise<string | null> {
    const refreshToken = selectRefreshToken(store.getState());
    if (!refreshToken)
        return null;

    try {
        const response = await axios.post(
            `${baseURL ?? ''}/auth/refresh`,
            { refreshToken },
            { validateStatus: () => true },
        );
        if (response.status !== 200 || !response.data?.token)
            return null;

        const { token, refreshToken: newRefreshToken } = response.data;
        store.dispatch(authSlice.actions.setAuth({ token, refreshToken: newRefreshToken }));
        return token as string;
    } catch {
        // Network failure talking to /auth/refresh itself - treat the same as "refresh failed",
        // falling back to the original 401 rather than throwing out of an interceptor.
        return null;
    }
}

export class HttpClient {
    private readonly http: AxiosInstance;
    constructor(config?: AxiosRequestConfig) {
        this.http = axios.create({
            withCredentials: true,
            ...config,
        });

        // Sep 2026 - silently renews the access token instead of surfacing a hard "Session
        // Expired" the moment the 60-minute token dies, as long as the refresh token (14 days,
        // server-side TokenService.RefreshTokenExpirationDays) is still good. Every SimplyTestApi
        // call sets `validateStatus: () => true` (see simplytest.ts), so a 401 response never
        // REJECTS the promise - it resolves normally with status 401 - which is why this hooks the
        // success (onFulfilled) interceptor path rather than the more familiar onRejected/catch-401
        // pattern used elsewhere for axios refresh interceptors.
        //
        // Deliberately transparent to every caller: on a successful refresh+retry, this resolves
        // with the RETRIED response (the caller never even sees the original 401). On a failed
        // refresh (refresh token missing/expired/revoked, or the caller was the refresh/signin call
        // itself), this resolves with the ORIGINAL 401 unchanged - every saga's own
        // `if (response.status === 401) { yield put(unauthorized(undefined)); }` check (duplicated
        // across ~60+ call sites) still fires exactly as it did before this existed, so none of
        // those call sites needed to change.
        this.http.interceptors.response.use(async (response: AxiosResponse) => {
            const original = response.config as RetriableRequestConfig;

            if (
                response.status !== 401 ||
                original._retriedAfterRefresh ||
                original.url?.includes('/auth/refresh') ||
                original.url?.includes('/auth/signin')
            ) {
                return response;
            }

            // If the store's current token has already moved past what THIS request was originally
            // sent with, some other request in the same burst already refreshed while this one was
            // in flight - just retry with the current token, no second refresh call needed.
            const requestToken = getBearerToken(original.headers);
            const currentToken = selectIdToken(store.getState());
            if (requestToken && currentToken && requestToken !== currentToken) {
                original._retriedAfterRefresh = true;
                setBearerToken(original, currentToken);
                return this.http.request(original);
            }

            if (!refreshPromise) {
                refreshPromise = performRefresh(original.baseURL).finally(() => {
                    refreshPromise = null;
                });
            }
            const newToken = await refreshPromise;
            if (!newToken)
                return response;

            original._retriedAfterRefresh = true;
            setBearerToken(original, newToken);
            return this.http.request(original);
        });
    }

    request<T = any, R = AxiosResponse<T>>(config: AxiosRequestConfig): Promise<R> {
        return this.http.request(config);
    }

    get<T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
        return this.http.get<T, R>(url, config);
    }

    post<T = any, R = any>(
        url: string,
        data?: T,
        config?: AxiosRequestConfig
    ): Promise<AxiosResponse<R>> {
        return this.http.post<T, AxiosResponse<R>>(url, data, config);
    }

    put<T = any, R = any>(
        url: string,
        data?: T,
        config?: AxiosRequestConfig
    ): Promise<AxiosResponse<R>> {
        return this.http.put<T, AxiosResponse<R>>(url, data, config);
    }

    delete<T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
        return this.http.delete<T, R>(url, config);
    }
}
